import { Check, X } from "lucide-react"
import { useIntl } from "react-intl"
import { cn } from "@/lib/utils"
import { CHOICE_LETTERS } from "./format"

/** One answer choice as a selectable card; after answering it can show correct / wrong. */
export function ChoiceOption({
  name,
  index,
  text,
  checked,
  disabled,
  state,
  onSelect,
}: {
  name: string
  index: number
  text: string
  checked: boolean
  disabled: boolean
  state: "correct" | "wrong" | null
  onSelect: () => void
}) {
  const intl = useIntl()
  const id = `${name}-choice-${index}`
  return (
    <label
      htmlFor={id}
      className={cn(
        "flex items-start gap-3 rounded-xl border border-hairline px-4 py-3 font-normal text-ink-body shadow-xs",
        "has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-accent",
        !disabled && "cursor-pointer hover:bg-surface",
        !state && checked && "border-accent bg-accent-faded",
        state === "correct" && "border-accent bg-accent-faded",
        state === "wrong" && "border-danger bg-danger-faded",
      )}
    >
      <input
        type="radio"
        id={id}
        name={name}
        value={index}
        checked={checked}
        disabled={disabled}
        onChange={onSelect}
        className="sr-only"
      />
      <span
        className={cn(
          "flex h-6 w-6 shrink-0 items-center justify-center rounded-full border text-xs font-medium",
          checked || state === "correct" ? "border-accent text-accent-display" : "border-hairline text-ink-muted",
          state === "wrong" && "border-danger text-danger-display",
        )}
        aria-hidden
      >
        {state === "correct" ? <Check className="h-3.5 w-3.5" /> : state === "wrong" ? <X className="h-3.5 w-3.5" /> : CHOICE_LETTERS[index]}
      </span>
      <span className="min-w-0 flex-1 break-words pt-0.5">{text}</span>
      {state === "correct" && <span className="sr-only">{intl.formatMessage({ id: "quizzes.sr_correct" })}</span>}
      {state === "wrong" && <span className="sr-only">{intl.formatMessage({ id: "quizzes.sr_yours" })}</span>}
    </label>
  )
}
